'use client'

import { useTenantButtonColor } from '@/components/TenantButtonColorContext'
import { darkenHex, getContrastTextColor, isValidHex } from '@/lib/colorUtils'
import { trackSurveyImpression } from '@/lib/survey'
import { submitSurveyForm } from '@/lib/survey'
import type { ScenarioNode, SurveyOption, SurveyQuestion } from '@/types/interactiveVideo'
import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react'

/** テナントカラー未設定時のヘッダー色（moovy ブルー・SurveySheet と同じ既定値） */
const DEFAULT_SURVEY_BRAND = '#2563EB'
import { canLeaveSurveyGate, getGateQuestions, isLeadValueValid, LEAD_PLACEHOLDER } from './surveyCue'
import type { RoutingAnswer } from './surveyRouting'

export interface SurveyGateProps {
  /** 描画対象の SURVEYノード（設問・遷移先を持つ） */
  node: ScenarioNode
  /** 回答保存 API に渡すシナリオID。オーサリングのプレビュー等では未指定で、その場合サーバ保存しない。 */
  scenarioId?: string
  /** 同一視聴セッションで回答済みの questionId 集合（再提示しないための除外） */
  answeredIds: ReadonlySet<string>
  /** 設問に回答したら通知する（セッションの answeredIds に加える） */
  onAnswered: (questionId: string) => void
  /**
   * 必須を満たして送信したら、回答（ルーティング解決用）とともに通知する。遷移先の解決は
   * ノードグラフを持つプレイヤー側が行う（回答→分岐 / 未該当・線形はデフォルト遷移先・#265）。
   */
  onComplete: (answers: RoutingAnswer[]) => void
}

type LocalAnswer = { optionIds: string[]; freeText: string; leadValue: string }

const emptyAnswer: LocalAnswer = { optionIds: [], freeText: '', leadValue: '' }

/**
 * POPUP の SURVEYノード（全画面ゲート・#263/#264）の描画。
 *
 * 動画のあいだに全画面フォームを即時提示し、必須設問を満たすまで外向き遷移をブロックする。
 * 非PII設問（SINGLE/MULTI/FREE）とリード（PII）項目（leadFieldKind 非null）を混在でき、送信で
 * フォーム全体を一括保存する（submitSurveyForm＝サーバ側で PII→リード表 / 非PII→回答表 へ atomic に
 * 振り分け）。回答済み設問は同一視聴セッションでは再提示しない（getGateQuestions で除外）。
 * ゲート通過可否の判定は純粋関数 canLeaveSurveyGate に委ね、権威的な必須検証はサーバにある。
 */
const SurveyGate: FC<SurveyGateProps> = ({ node, scenarioId, answeredIds, onAnswered, onComplete }) => {
  const gateQuestions = useMemo(
    () => getGateQuestions(node.surveyQuestions ?? [], answeredIds),
    [node.surveyQuestions, answeredIds],
  )

  const [answers, setAnswers] = useState<Record<string, LocalAnswer>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getAnswer = (questionId: string): LocalAnswer => answers[questionId] ?? emptyAnswer

  const isAnswered = (question: SurveyQuestion): boolean => {
    const answer = getAnswer(question.id)
    // リード（PII）項目は入力値の有効性で判定（EMAIL は形式検証を継承）。
    if (question.leadFieldKind !== null) {
      return isLeadValueValid(question.leadFieldKind, answer.leadValue)
    }
    return question.questionType === 'FREE'
      ? answer.freeText.trim().length > 0
      : answer.optionIds.length > 0
  }

  // ローカルで有効な回答を持つ設問 id 集合。必須ゲート判定（canLeaveSurveyGate）に使う。
  const locallyAnswered = new Set(gateQuestions.filter(isAnswered).map((q) => q.id))
  const canSubmit = canLeaveSurveyGate(gateQuestions, locallyAnswered)

  // 提示すべき設問が無い（回答済みや設問ゼロ）ゲートは通過扱いにして遷移させる。
  // 回答が無いためルーティングは効かず、プレイヤー側で常にデフォルト遷移先へ解決される（#265）。
  const completedRef = useRef(false)
  useEffect(() => {
    if (gateQuestions.length === 0 && !completedRef.current) {
      completedRef.current = true
      onComplete([])
    }
  }, [gateQuestions.length, onComplete])

  // ゲート到達を計測（到達→完了ファネル）。ゲートが実際に表示された時＝提示すべき設問がある時に
  // 1 配置につき 1 回送る（nodeKey 変化で再送・回答済み再ゲートや空ゲートは対象外）。プレビュー
  // （scenarioId 未指定）はサーバ保存しない。完了は送信（submitSurveyForm）で表現する。
  // 記録先は SurveyImpression に一本化した（動画紐づきアンケートと同じ軸で見るため・ADR-0015）。
  useEffect(() => {
    if (scenarioId && gateQuestions.length > 0) {
      trackSurveyImpression({ scenarioId, nodeKey: node.nodeKey })
    }
  }, [scenarioId, node.nodeKey, gateQuestions.length])

  const setSingle = (questionId: string, optionId: string) =>
    setAnswers((prev) => ({ ...prev, [questionId]: { optionIds: [optionId], freeText: '', leadValue: '' } }))

  const toggleMulti = (questionId: string, option: SurveyOption) =>
    setAnswers((prev) => {
      const current = prev[questionId]?.optionIds ?? []
      const next = current.includes(option.id)
        ? current.filter((id) => id !== option.id)
        : [...current, option.id]
      return { ...prev, [questionId]: { optionIds: next, freeText: '', leadValue: '' } }
    })

  const setFree = (questionId: string, freeText: string) =>
    setAnswers((prev) => ({ ...prev, [questionId]: { optionIds: [], freeText, leadValue: '' } }))

  const setLead = (questionId: string, leadValue: string) =>
    setAnswers((prev) => ({ ...prev, [questionId]: { optionIds: [], freeText: '', leadValue } }))

  // ローカル回答をサーバ送信用の1回答へ変換する（リード項目は leadValue、非PIIは選択肢/自由記述）。
  const toFormAnswer = (question: SurveyQuestion) => {
    const answer = getAnswer(question.id)
    if (question.leadFieldKind !== null) {
      return { questionId: question.id, optionIds: [], freeText: null, leadValue: answer.leadValue.trim() }
    }
    return question.questionType === 'FREE'
      ? { questionId: question.id, optionIds: [], freeText: answer.freeText.trim(), leadValue: null }
      : { questionId: question.id, optionIds: answer.optionIds, freeText: null, leadValue: null }
  }

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return
    setIsSubmitting(true)
    try {
      // 回答済み設問のみを1リクエストで一括送信する（未回答の任意設問は送らない）。必須は
      // canSubmit で担保済み。サーバ側で PII→リード表 / 非PII→回答表 へ atomic に振り分けられる。
      // scenarioId 未指定（プレビュー）ではサーバ保存せず、遷移だけ行う。
      const answeredQuestions = gateQuestions.filter((question) => locallyAnswered.has(question.id))
      if (scenarioId && answeredQuestions.length > 0) {
        await submitSurveyForm({
          scenarioId,
          nodeKey: node.nodeKey,
          answers: answeredQuestions.map(toFormAnswer),
        })
      }
      for (const question of answeredQuestions) {
        onAnswered(question.id)
      }
      // 送信した回答をプレイヤーへ渡す。遷移先の解決（ルーティング設問→分岐 / 未該当・線形→デフォルト・#265）は
      // ノードグラフを持つプレイヤー側が行う。
      const routingAnswers: RoutingAnswer[] = answeredQuestions.map((question) => ({
        questionId: question.id,
        optionIds: getAnswer(question.id).optionIds,
      }))
      onComplete(routingAnswers)
    } finally {
      setIsSubmitting(false)
    }
  }

  // ヘッダー・選択時の色はテナントの CTA ボタン色に合わせる（未設定・不正値なら既定のブルー）。
  // 明るい色を設定されても読めるよう、ヘッダーの文字色は明度から自動判定する（colorUtils）。
  const tenantColor = useTenantButtonColor()
  const brand = tenantColor !== null && isValidHex(tenantColor) ? tenantColor : DEFAULT_SURVEY_BRAND
  const heroForeground = getContrastTextColor(brand)
  const gateVars = {
    '--mg-brand': brand,
    '--mg-brand-deep': darkenHex(brand, 0.28),
    '--mg-fg': heroForeground,
    '--mg-soft': heroForeground === '#000000' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.24)',
  } as CSSProperties

  return (
    <div data-testid="survey-gate" style={gateVars} className="pointer-events-auto flex h-full flex-col overflow-hidden bg-[#f7f8fb]">
      <style>{`
        /* ブランドヘッダー: テナントカラーの面にタイトルを白抜きで置く（SurveySheet と同じ考え方） */
        .moovy-gate-hero {
          background-image: linear-gradient(135deg, var(--mg-brand) 0%, var(--mg-brand-deep) 100%);
          color: var(--mg-fg);
        }
        .moovy-gate-label { background: var(--mg-soft); color: var(--mg-fg); }

        /* 回答カード: 白い面を浮かせて「押せるもの」に見せる */
        .moovy-gate-opt {
          box-shadow: 0 2px 6px rgba(17, 24, 39, 0.07);
          transition: box-shadow 160ms ease, transform 160ms ease, color 160ms ease;
        }
        .moovy-gate-opt:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(17, 24, 39, 0.14);
        }
        .moovy-gate-opt:hover .moovy-gate-tick { background: var(--mg-brand); color: #fff; }
        .moovy-gate-opt[data-on="true"] {
          color: var(--mg-brand-deep);
          font-weight: 700;
          box-shadow: inset 0 0 0 2px var(--mg-brand), 0 6px 16px rgba(17, 24, 39, 0.14);
        }
        .moovy-gate-opt[data-on="true"] .moovy-gate-tick { background: var(--mg-brand); color: #fff; }
        .moovy-gate-tick {
          background: #eef1f6;
          color: transparent;
          transition: background-color 160ms ease, color 160ms ease;
        }

        .moovy-gate-field { box-shadow: 0 2px 6px rgba(17, 24, 39, 0.07); }
        .moovy-gate-field:focus { border-color: var(--mg-brand); }

        .moovy-gate-submit { background: var(--mg-brand); transition: filter 160ms ease; }
        .moovy-gate-submit:not(:disabled):hover { filter: brightness(1.08); }
        .moovy-gate-submit:disabled { background: #d1d5db; cursor: not-allowed; }
      `}</style>

      {/* ブランドヘッダー */}
      <div className="moovy-gate-hero flex-shrink-0 px-6 pb-6 pt-5">
        <span className="moovy-gate-label rounded-full px-2.5 py-1 text-[11px] font-bold">アンケート</span>
        {node.title && <p className="mt-2.5 text-[19px] font-bold leading-relaxed">{node.title}</p>}
      </div>

      {/* 設問リスト（スクロール可能） */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="flex flex-col gap-6">
          {gateQuestions.map((question, index) => (
            <div key={question.id} data-testid="survey-gate-question">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-[13px] font-bold text-gray-500">設問 {index + 1}</span>
                {question.required && (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-500">必須</span>
                )}
              </div>
              <p className="mb-3 text-[17px] font-bold leading-relaxed text-gray-900">{question.text}</p>

              {/* リード（PII）項目: questionType を無視して PII 入力欄を出す（値はリード表へ隔離保存・ADR-0012）。 */}
              {question.leadFieldKind !== null && (
                <input
                  type={question.leadFieldKind === 'EMAIL' ? 'email' : 'text'}
                  inputMode={question.leadFieldKind === 'EMAIL' ? 'email' : undefined}
                  autoComplete={question.leadFieldKind === 'EMAIL' ? 'email' : undefined}
                  data-testid="survey-gate-lead-input"
                  value={getAnswer(question.id).leadValue}
                  onChange={(event) => setLead(question.id, event.target.value)}
                  placeholder={LEAD_PLACEHOLDER[question.leadFieldKind]}
                  className="moovy-gate-field w-full rounded-[13px] border border-[#e3e7ee] bg-white px-4 py-3.5 text-[16px] text-gray-800 outline-none transition-colors placeholder:text-[#4b5563] placeholder:opacity-100"
                />
              )}

              {question.leadFieldKind === null && question.questionType === 'SINGLE' && (
                <div className="flex flex-col gap-2.5">
                  {question.options.map((option) => {
                    const checked = getAnswer(question.id).optionIds.includes(option.id)
                    return (
                      <button
                        key={option.id}
                        type="button"
                        data-testid="survey-gate-option"
                        aria-pressed={checked}
                        data-on={checked}
                        onClick={() => setSingle(question.id, option.id)}
                        className="moovy-gate-opt flex w-full items-center justify-between gap-3 rounded-[13px] bg-white px-[18px] py-4 text-left text-[16px] font-medium text-gray-800"
                      >
                        <span>{option.label}</span>
                        <span className="moovy-gate-tick flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full">
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {question.leadFieldKind === null && question.questionType === 'MULTI' && (
                <div className="flex flex-col gap-2.5">
                  {question.options.map((option) => {
                    const checked = getAnswer(question.id).optionIds.includes(option.id)
                    return (
                      <button
                        key={option.id}
                        type="button"
                        data-testid="survey-gate-option"
                        aria-pressed={checked}
                        data-on={checked}
                        onClick={() => toggleMulti(question.id, option)}
                        className="moovy-gate-opt flex w-full items-center justify-between gap-3 rounded-[13px] bg-white px-[18px] py-4 text-left text-[16px] font-medium text-gray-800"
                      >
                        <span>{option.label}</span>
                        <span className="moovy-gate-tick flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-[7px]">
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {question.leadFieldKind === null && question.questionType === 'FREE' && (
                <textarea
                  data-testid="survey-gate-free-input"
                  value={getAnswer(question.id).freeText}
                  onChange={(event) => setFree(question.id, event.target.value)}
                  rows={4}
                  placeholder="ご自由にご記入ください"
                  className="survey-free-answer moovy-gate-field w-full resize-none rounded-[13px] border border-[#e3e7ee] bg-white px-4 py-3.5 text-[16px] text-gray-800 outline-none transition-colors placeholder:text-[#4b5563] placeholder:opacity-100"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 送信（必須を満たすまで無効＝遷移ブロック） */}
      <div className="flex-shrink-0 border-t border-[#e6e9f0] bg-white px-5 py-4">
        <button
          type="button"
          data-testid="survey-gate-submit"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="moovy-gate-submit w-full rounded-xl px-4 py-3.5 text-[16px] font-bold text-white"
        >
          {isSubmitting ? '送信中...' : '送信して次へ'}
        </button>
        {!canSubmit && (
          <p className="mt-2 text-center text-[11px] text-gray-400">必須の設問に回答すると次へ進めます</p>
        )}
      </div>
    </div>
  )
}

export default SurveyGate
